import React, { useCallback, useRef } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { useAnalytics } from 'services/analytics/useAnalytics';
import { calcSizeForDevice } from 'utils/index';
import { Platform } from 'utils/platform';

import { useHorizontalPagination } from '../helpers/pagination';
import SectionArrows from './SectionArrows';
import SectionHorizontalSkeleton from './SectionHorizontalSkeleton';

import FocusView from 'components/FocusView';

type SectionHorizontalProps = CmsHomeSection & {
  onEndReached?: () => void;
  requestTVFocus?: boolean;
};

const SectionHorizontal: React.FC<SectionHorizontalProps> = (props) => {
  const analytics = useAnalytics();

  const { ref, onScroll, goForward, renderItem, goBackward, canGoForward, canGoBackward } =
    useHorizontalPagination({
      renderItem: props.renderItem,
      items: props.items,
    });

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 95,
  }).current;

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      viewableItems.forEach((viewableItem) => {
        if (viewableItem.isViewable) {
          // analytics.triggerViewProgramListEvent({
          //   programTitle: viewableItem.item.title || viewableItem.item.image.title,
          //   programListName: viewableItem.item.reference,
          // });
          return;
        }
      });
    },
    []
  );

  if (props.loading) {
    return <SectionHorizontalSkeleton {...props} />;
  }

  if (!props.items || !props.items.length) {
    return null;
  }

  const list = (
    <FocusView>
      <FlatList
        ref={ref}
        focusable={false}
        data={props.items}
        onScroll={onScroll}
        maxToRenderPerBatch={3}
        renderItem={renderItem}
        showsVerticalScrollIndicator={false}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ flexGrow: 1, marginBottom: calcSizeForDevice(15, 30) }}
        horizontal={props.variant !== 'section.boxed'}
        keyExtractor={(item, index) => `${item.link}_${index.toString()}`}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
      />
    </FocusView>
  );

  const onPress = (side: 'left' | 'right') => {
    side === 'left' ? goBackward() : goForward();
    analytics.triggerShelfScrollActionEvent({ scrollSide: side });
  };

  return (
    <View style={styles.container}>
      {list}
      {!Platform.isTV && (
        <SectionArrows side="left" onPress={() => onPress('left')} isVisible={canGoBackward} />
      )}

      {!Platform.isTV && (
        <SectionArrows side="right" onPress={() => onPress('right')} isVisible={canGoForward} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
});

export default SectionHorizontal;
